import React, { useMemo, } from 'react';
import { Button, } from 'reactstrap';
import DatePicker from 'react-datepicker';
import { useAsync, } from 'react-use';
import { useHistory, useLocation, } from 'react-router';
import { keyBy, keys, entries, isPlainObject, isArray, isEmpty, } from 'lodash';
import { format as formatDate, startOfDay, endOfDay, subDays, parseISO, isValid, } from 'date-fns';

import { functions } from '../../firebase';
import { fullPathWithParams, } from '../../utils';
import { operationLogCollections, operationLogTypes, operationLogAuthTypes, normalizeOperationLog, operationLogTargetLabel, } from '../../shared/models/operationLog';
import SettingsPage from '../hocs/SettingsPage';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import QuerySelector from '../QuerySelector';
import ModalButton from '../ModalButton';

const getCompanyMembers = functions.httpsCallable('getCompanyMembers');
const pageSize = 100;
const operationTypeOptions = entries(operationLogTypes).map(([value, { label }]) => ({ label, value, }));
const collectionOptions = entries(operationLogCollections).map(([value, { label }]) => ({ label, value, }));

const formatValue = (value) => {
  if(value == null) return '';
  if(typeof value.toDate === 'function') return formatDate(value.toDate(), 'yyyy/MM/dd HH:mm:ss');
  if(isPlainObject(value) || isArray(value)) return JSON.stringify(value, (k, v) => typeof v?.toDate === 'function' ? formatDate(v.toDate(), 'yyyy/MM/dd HH:mm:ss') : v, 2);
  return String(value);
};

const isValidDateString = _ => isValid(parseISO(_));

export default SettingsPage(function CompanyOperationLogs (props) {
  const { company, user, } = props;
  const history = useHistory();
  const location = useLocation();
  const {
    logOperationTypes = keys(operationLogTypes),
    logCollection,
    logUserId,
    logStartDate: _logStartDate,
    logEndDate: _logEndDate,
    logLimit = pageSize,
  } = useQueryParams();
  const logStartDate = isValidDateString(_logStartDate) ? _logStartDate : formatDate(subDays(new Date(), 30), 'yyyy-MM-dd');
  const logEndDate = isValidDateString(_logEndDate) ? _logEndDate : formatDate(new Date(), 'yyyy-MM-dd');
  const startDate = parseISO(logStartDate);
  const endDate = parseISO(logEndDate);
  const limit = Number(logLimit);
  const { value: members = [], } = useAsync(async () => (await getCompanyMembers({ companyId: company.id })).data, [company.id]);
  const membersById = keyBy([user, ...members], 'id');
  const userOptions = members.map(_ => ({ label: _.displayName || _.email, value: _.id, }));
  const logsRef = useMemo(() => {
    const ref = company.ref.collection('logs')
      .where('operationType', 'in', isEmpty(logOperationTypes) ? keys(operationLogTypes) : logOperationTypes)
      .where('createdAt', '>=', startOfDay(parseISO(logStartDate)))
      .where('createdAt', '<=', endOfDay(parseISO(logEndDate)));
    const collectionFilteredRef = logCollection ? ref.where('collection', '==', logCollection) : ref;
    const userFilteredRef = logUserId ? collectionFilteredRef.where('authId', '==', logUserId) : collectionFilteredRef;
    return userFilteredRef.orderBy('createdAt', 'desc').limit(limit);
  }, [company.id, JSON.stringify(logOperationTypes), logCollection, logUserId, logStartDate, logEndDate, limit]);
  const { items: _logs, isLoading, } = useCollectionSubscription(logsRef, [logsRef], { detail: true });
  const logs = _logs.map(normalizeOperationLog);
  const replaceParams = (params) => history.replace(encodeURI(fullPathWithParams(params, location)));
  // ブラウザからの操作は authType が api_key になり、authId にユーザーのuidが入る
  const operatorLabel = ({ authType, authId, authDisplayName, }) => {
    const member = membersById[authId];
    if(member) return member.displayName || member.email;
    if(authDisplayName) return authDisplayName;
    if(authType === 'api_key' && authId) return authId;

    return operationLogAuthTypes[authType]?.label || authType || '-';
  };

  return props.translate(
    <div className="company-operation-logs">
      <div className="d-flex justify-content-center mb-2">
        <h4>操作ログ</h4>
      </div>
      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <QuerySelector paramName="logOperationTypes" label="操作種別" isMulti options={operationTypeOptions} width={250} selectorProps={{ placeholder: 'すべて' }} />
        <QuerySelector paramName="logCollection" label="対象" options={collectionOptions} width={250} selectorProps={{ placeholder: 'すべて' }} />
        <QuerySelector paramName="logUserId" label="ユーザー" options={userOptions} width={250} selectorProps={{ placeholder: 'すべて' }} />
        <div>
          <div className="text-muted small">期間</div>
          <div className="d-flex align-items-center gap-1">
            <DatePicker dateFormat="yyyy/MM/dd" className="form-control" selected={startDate} selectsStart startDate={startDate} endDate={endDate} maxDate={endDate} isClearable onChange={_ => replaceParams({ logStartDate: _ && formatDate(_, 'yyyy-MM-dd'), logLimit: pageSize, })} />
            <span>〜</span>
            <DatePicker dateFormat="yyyy/MM/dd" className="form-control" selected={endDate} selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} isClearable onChange={_ => replaceParams({ logEndDate: _ && formatDate(_, 'yyyy-MM-dd'), logLimit: pageSize, })} />
          </div>
        </div>
      </div>
      <table className="table table-bordered table-sm">
        <thead className="thead-light text-nowrap">
          <tr>
            <th>日時</th>
            <th>操作種別</th>
            <th>対象</th>
            <th>ドキュメント</th>
            <th>ユーザー</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {
            logs.map((log) => {
              const { id, operationType, collection, documentId, createdAt, before, after, } = log;
              const changedKeys = keys({ ...before, ...after });
              return (
                <tr key={id}>
                  <td className="text-nowrap">{createdAt && formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}</td>
                  <td><span className={`badge badge-${operationLogTypes[operationType]?.color}`}>{operationLogTypes[operationType]?.label}</span></td>
                  <td>{operationLogTargetLabel(collection)}</td>
                  <td>{(after || before)?.name || documentId}</td>
                  <td>{operatorLabel(log)}</td>
                  <td>
                    <ModalButton size="sm" color="link" className="p-0" operationType="read" title="変更内容" label="詳細" modalProps={{ size: 'lg' }} content={_ => (
                      <table className="table table-bordered table-sm">
                        <thead className="thead-light">
                          <tr>
                            <th>項目</th>
                            <th>変更前</th>
                            <th>変更後</th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            changedKeys.map(key => (
                              <tr key={key}>
                                <td>{key}</td>
                                <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{formatValue(before?.[key])}</td>
                                <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{formatValue(after?.[key])}</td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                    )} />
                  </td>
                </tr>
              );
            })
          }
        </tbody>
      </table>
      {isLoading && <div className="text-center"><span className="fas fa-spin fa-spinner" /></div>}
      {!isLoading && isEmpty(logs) && <div className="text-center text-muted">操作ログはありません</div>}
      {
        !isLoading && logs.length >= limit && (
          <div className="text-center">
            <Button onClick={_ => replaceParams({ logLimit: limit + pageSize, })}>さらに読み込む</Button>
          </div>
        )
      }
    </div>
  );
});
